import { AWSWafError } from './error';

// 重试配置
const MAX_RETRIES = 3;
const BASE_RETRY_DELAY = 1000; // 基础延迟时间，单位毫秒

// 计算指数退避延迟时间
const getExponentialDelay = (retryCount: number): number => {
  return BASE_RETRY_DELAY * 2 ** retryCount;
};

/**
 * 动态重试加载脚本
 * @param url 脚本 URL
 * @param retryCount 当前重试次数
 * @returns 加载成功返回，失败抛出错误
 */
export const loadScript = async (url: string, retryCount = 0): Promise<void> => {
  try {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        document.body.removeChild(script);
        reject();
      };
      document.body.appendChild(script);
    });
  } catch {
    if (retryCount < MAX_RETRIES - 1) {
      const delay = getExponentialDelay(retryCount);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return loadScript(url, retryCount + 1);
    }

    // 超过最大重试次数，抛出错误
    throw new AWSWafError(`Failed to load ${url} script after multiple retries`, 'load_failed');
  }
};

/**
 * 清除 AWS WAF token cookie
 * @description 当遇到 202 Challenge 错误时，清除无效的 token cookie，以便重新获取新的 token
 */
export const clearAwsWafCookie = (): void => {
  // 只在浏览器环境中执行
  if (typeof document === 'undefined') {
    return;
  }

  try {
    const cookieName = 'aws-waf-token';
    const domain = window.location.hostname;
    const expiredDate = 'Thu, 01 Jan 1970 00:00:00 GMT';

    // 清除不同路径和域名的 cookie，确保清除成功
    // 1. 当前路径（根路径）
    // biome-ignore lint/suspicious/noDocumentCookie: clearing legacy AWS WAF cookie requires direct document.cookie assignment
    document.cookie = `${cookieName}=; path=/; expires=${expiredDate}; secure`;

    // 2. 根域名（如果存在，例如 .vidu.studio）
    if (domain.includes('.')) {
      const rootDomain = `.${domain.split('.').slice(-2).join('.')}`;
      // biome-ignore lint/suspicious/noDocumentCookie: clearing legacy AWS WAF cookie requires direct document.cookie assignment
      document.cookie = `${cookieName}=; path=/; domain=${rootDomain}; expires=${expiredDate}; secure`;
    }

    // 3. 当前完整域名
    // biome-ignore lint/suspicious/noDocumentCookie: clearing legacy AWS WAF cookie requires direct document.cookie assignment
    document.cookie = `${cookieName}=; path=/; domain=${domain}; expires=${expiredDate}; secure`;
  } catch (error) {
    // 静默处理错误，避免影响主流程
    console.warn('Failed to clear AWS WAF cookie:', error);
  }
};
